import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Bookmark,
  Filter,
  Search,
  Sparkles,
  User,
  Users,
  Zap,
} from "lucide-react";
import defaultAvatar from "../../utils/profile.png";
import { getFormattedDate } from "../../utils/formatDate";
import { useScoutContactRequestsMap } from "../../hooks/queries/useContactRequests";
import ContactRequestModal from "./ContactRequestModal";
import FeaturedBadge from "../plans/FeaturedBadge";
import {
  FOOTBALL_POSITIONS,
  FOOTBALL_SPORT_LABEL,
  athletePositionCode,
} from "../../constants/footballTaxonomy";
import {
  getScoutContactAction,
  scoutThreadPath,
} from "../../utils/scoutContactAction";

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const uniqueOptions = (items, key) =>
  [
    ...new Set(
      items
        .map((item) => item?.[key])
        .filter((value) => String(value || "").trim()),
    ),
  ].sort((a, b) => String(a).localeCompare(String(b), "es"));

const FOOTBALL_POSITION_OPTIONS = FOOTBALL_POSITIONS.map((p) => [p.code, p.label]);

const getAthleteId = (athlete) =>
  athlete?.athlete_id || athlete?.athleteId || athlete?.id;

const AthleteTile = ({ athlete, contactRequest, onContact }) => {
  const name = athlete?.full_name || "Sin nombre";
  const sport = athlete?.sport || FOOTBALL_SPORT_LABEL;
  const category = athlete?.category || "Categoria";
  const position = athlete?.position || "Equipo";
  // "—" y no 87 / 80: eran valores de relleno que se leían como datos reales,
  // así que un perfil vacío parecía un atleta de 80 años con 87 puntos. Un
  // guion dice "no hay dato", que es la verdad.
  const points = athlete?.points ?? "—";
  const age = athlete?.age ?? "—";
  const matchScore = typeof athlete?.score === "number" ? athlete.score : null;
  const reasons = Array.isArray(athlete?.reasons) ? athlete.reasons : [];
  const contactAction = getScoutContactAction(contactRequest);

  return (
    <article className="bg-white border border-gray-200 shadow-sm overflow-hidden">
      <div className="relative aspect-[4/3] bg-gray-100">
        <img
          src={athlete?.avatar_url || defaultAvatar}
          alt={name}
          onError={(event) => {
            event.currentTarget.src = defaultAvatar;
          }}
          className="h-full w-full object-cover"
        />
        {matchScore !== null && matchScore > 0 && (
          <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-blue-900/90 px-2 py-1 text-[10px] font-bold text-white shadow-sm">
            <Sparkles size={10} />
            Match {matchScore}
          </span>
        )}
        <button
          type="button"
          className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-blue-700 shadow-sm"
          aria-label="Guardar atleta"
        >
          <Bookmark size={14} fill="currentColor" />
        </button>
      </div>

      <div className="p-3">
        <div className="flex items-center gap-1.5">
          <h3 className="truncate text-sm font-semibold text-gray-900">{name}</h3>
          {athlete?.featured === true && <FeaturedBadge />}
        </div>
        <p className="mt-0.5 truncate text-[11px] text-gray-500">
          {category} - {position}
        </p>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-700">
            {sport}
          </span>
          {reasons.length > 0 ? (
            reasons.slice(0, 3).map((reason, index) => (
              <span
                key={`${reason}-${index}`}
                className="rounded-full bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-700"
              >
                {reason}
              </span>
            ))
          ) : (
            ["x", "x", "x", "x"].map((label, index) => (
              <span
                key={`${label}-${index}`}
                className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[10px] font-semibold text-blue-500"
              >
                {label}
              </span>
            ))
          )}
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 text-[11px] text-gray-600">
            <span className="flex items-center gap-1">
              <User size={12} />
              {age}
            </span>
            <span className="flex items-center gap-1">
              <Zap size={12} />
              {points}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onContact?.(athlete)}
            disabled={contactAction.disabled}
            className="min-h-11 bg-blue-900 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-100 disabled:text-blue-900"
          >
            {contactAction.label}
          </button>
        </div>
      </div>
    </article>
  );
};

const AthleteSearchPage = ({
  athletes = [],
  displayName = "Scout",
  searches = [],
  activeSearchId = null,
  onActiveSearchChange,
  onGoToSearches,
  onNotificationsClick,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [position, setPosition] = useState("all");
  const [gender, setGender] = useState("all");
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { data: contactRequestsByAthleteId = {} } =
    useScoutContactRequestsMap();
  const navigate = useNavigate();

  const handleContact = (athlete) => {
    const request = contactRequestsByAthleteId[getAthleteId(athlete)];
    if (getScoutContactAction(request).kind === "message") {
      navigate(scoutThreadPath(request));
      return;
    }
    setSelectedAthlete(athlete);
    setIsContactModalOpen(true);
  };

  const hasMatchScores = useMemo(
    () => athletes.some((a) => typeof a?.score === "number" && a.score > 0),
    [athletes],
  );
  const [sortBy, setSortBy] = useState("recent");
  useEffect(() => {
    if (hasMatchScores) {
      setSortBy((prev) => (prev === "recent" ? "match" : prev));
    } else {
      setSortBy((prev) => (prev === "match" ? "recent" : prev));
    }
  }, [hasMatchScores]);
  const activeSearch = useMemo(
    () => searches.find((s) => s.id === activeSearchId) || null,
    [searches, activeSearchId],
  );

  // Banner en positivo (Scout QA #23): nunca decimos "no se encontró nada".
  // El matching siempre devuelve algo ordenado por afinidad, así que un score
  // bajo no es "sin resultados" — es "esto es lo más cercano". Decir lo primero
  // sugiere que el scout no tiene a quién mirar, cuando sí lo tiene.
  const resultsBanner = useMemo(() => {
    if (!activeSearch) return null;
    if (!activeSearch.criteria) {
      return {
        title: "Explora los atletas disponibles",
        message:
          "Aún estamos afinando los criterios de esta búsqueda. Mientras tanto, aquí tienes los atletas disponibles — vuelve a generar la búsqueda para ver los mejores matches.",
      };
    }
    // Sin match exacto: nadie puntúa por encima del mínimo que da el simple
    // hecho de tener el perfil completo.
    const hasStrongMatch = athletes.some((a) => (a?.score ?? 0) > 25);
    if (!hasStrongMatch && athletes.length > 0) {
      return {
        title: "Las mejores opciones para tu búsqueda",
        message:
          "No hay coincidencias exactas con todos tus criterios, pero estos son los atletas más cercanos a lo que buscas, ordenados por relevancia.",
      };
    }
    return null;
  }, [activeSearch, athletes]);

  const genderOptions = useMemo(() => uniqueOptions(athletes, "gender"), [athletes]);

  const filteredAthletes = useMemo(() => {
    let list = [...athletes];

    if (searchTerm.trim()) {
      const query = normalizeText(searchTerm);
      list = list.filter((athlete) =>
        normalizeText(athlete?.full_name).includes(query),
      );
    }

    // Solo fútbol: sin filtro de deporte, y las posiciones son las de la
    // taxonomía (el texto libre legado se mapea a su code).
    if (position !== "all") {
      list = list.filter((athlete) => athletePositionCode(athlete) === position);
    }

    if (gender !== "all") {
      list = list.filter(
        (athlete) => normalizeText(athlete?.gender) === normalizeText(gender),
      );
    }

    list.sort((a, b) => {
      if (sortBy === "match") {
        const sa = typeof a?.score === "number" ? a.score : -1;
        const sb = typeof b?.score === "number" ? b.score : -1;
        return sb - sa;
      }
      if (sortBy === "points") return (b?.points || 0) - (a?.points || 0);
      if (sortBy === "name") {
        return (a?.full_name || "").localeCompare(b?.full_name || "", "es");
      }
      return new Date(b?.created_at || 0) - new Date(a?.created_at || 0);
    });

    return list;
  }, [athletes, gender, position, searchTerm, sortBy]);

  const selects = [
    {
      ariaLabel: "Filtrar por posición",
      value: position,
      onChange: setPosition,
      options: [
        ["all", "Todas las posiciones"],
        ...FOOTBALL_POSITION_OPTIONS,
      ],
    },
    {
      ariaLabel: "Filtrar por género",
      value: gender,
      onChange: setGender,
      options: [["all", "Genero"], ...genderOptions.map((item) => [item, item])],
    },
    {
      ariaLabel: "Ordenar atletas",
      value: sortBy,
      onChange: setSortBy,
      options: [
        ...(hasMatchScores ? [["match", "Mejor match"]] : []),
        ["recent", "Más recientes"],
        ["points", "Mayor puntaje"],
        ["name", "Nombre A-Z"],
      ],
    },
  ];

  if (!activeSearchId) {
    return (
      <main className="flex-1 overflow-y-auto bg-gray-50">
        <div className="px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500">
                {getFormattedDate()}
              </p>
              <h1 className="mt-1 text-xl font-bold text-gray-900">
                Listado de atletas
              </h1>
            </div>
            <div className="hidden lg:flex items-center gap-3 text-blue-950">
              <Search size={19} />
              <button
                type="button"
                onClick={onNotificationsClick}
                className="p-1.5 hover:bg-white"
                aria-label="Notificaciones"
              >
                <Bell size={18} />
              </button>
              <User size={18} />
            </div>
          </div>
          <div className="mt-10 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
            <Sparkles size={36} className="text-gray-300" />
            <p className="mt-3 text-base font-semibold text-gray-700">
              No tienes una búsqueda activa
            </p>
            <p className="mt-1 max-w-md text-sm text-gray-500">
              Crea o selecciona una búsqueda para ver los atletas rankeados.
            </p>
            <button
              type="button"
              onClick={onGoToSearches}
              className="mt-4 rounded-lg bg-blue-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
            >
              Ir a mis búsquedas
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 overflow-y-auto bg-gray-50">
      <div className="px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-7">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-500">{getFormattedDate()}</p>
            <div className="mt-1 flex flex-wrap items-baseline gap-3">
              <h1 className="text-xl font-bold text-gray-900">
                {activeSearch?.title || "Búsqueda"}
              </h1>
              {searches.length > 1 && (
                <select
                  value={activeSearchId || ""}
                  onChange={(event) =>
                    onActiveSearchChange?.(Number(event.target.value))
                  }
                  className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:border-blue-500 focus:outline-none"
                  aria-label="Cambiar de búsqueda"
                >
                  {searches.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={onGoToSearches}
                className="text-xs font-medium text-blue-700 hover:underline"
              >
                Gestionar búsquedas
              </button>
            </div>
            {activeSearch?.recruiting_brief && (
              <p className="mt-2 line-clamp-2 max-w-3xl text-sm text-gray-600">
                {activeSearch.recruiting_brief}
              </p>
            )}
            {/* Only present when the agent had to widen or narrow the search. */}
            {activeSearch?.agent_summary?.search_notes && (
              <p className="mt-2 max-w-3xl rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {activeSearch.agent_summary.search_notes}
              </p>
            )}
          </div>

          <div className="hidden lg:flex items-center gap-3 text-blue-950">
            <Search size={19} />
            <button
              type="button"
              onClick={onNotificationsClick}
              className="p-1.5 hover:bg-white"
              aria-label="Notificaciones"
            >
              <Bell size={18} />
            </button>
            <User size={18} />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:max-w-xs">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar talento..."
              className="h-10 w-full border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-800 outline-none focus:border-blue-700"
            />
          </div>

          {selects.map((select) => (
            <label
              key={select.value + select.options[0][1]}
              className="relative flex-1 min-w-[140px] sm:flex-none"
            >
              <select
                aria-label={select.ariaLabel}
                value={select.value}
                onChange={(event) => select.onChange(event.target.value)}
                className="h-10 w-full sm:min-w-[150px] appearance-none border border-gray-300 bg-white px-3 pr-9 text-xs text-gray-800 outline-none focus:border-blue-700"
              >
                {select.options.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <Filter
                size={14}
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-800"
              />
            </label>
          ))}

          <span className="ml-auto flex items-center gap-2 rounded-full bg-emerald-100 px-4 py-2 text-xs font-bold text-emerald-700">
            <Zap size={14} />
            {athletes.length} Nuevos Talentos
          </span>
        </div>

        {resultsBanner && (
          <div className="mt-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            <Sparkles size={18} className="mt-0.5 flex-shrink-0 text-blue-600" />
            <div>
              <p className="font-semibold">{resultsBanner.title}</p>
              <p className="mt-0.5 text-blue-800">{resultsBanner.message}</p>
            </div>
          </div>
        )}


        <section className="mt-5 border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-gray-900">
              {resultsBanner ? "Mejores opciones para ti" : "Todos los atletas"}
            </h2>
            <p className="text-xs text-gray-500">
              {filteredAthletes.length} resultado
              {filteredAthletes.length === 1 ? "" : "s"}
            </p>
          </div>

          {filteredAthletes.length === 0 ? (
            <div className="flex min-h-[240px] flex-col items-center justify-center text-center">
              <Users size={36} className="text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-500">
                No hay atletas con esos filtros
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {filteredAthletes.map((athlete) => (
                <AthleteTile
                  key={getAthleteId(athlete)}
                  athlete={athlete}
                  contactRequest={
                    contactRequestsByAthleteId[getAthleteId(athlete)]
                  }
                  onContact={handleContact}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <ContactRequestModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        athlete={selectedAthlete}
      />
    </main>
  );
};

export default AthleteSearchPage;
